/**
 * Entrada e registo. Um ecrã, dois modos.
 *
 * O token nunca passa por aqui: a API responde com `Set-Cookie` httpOnly e o
 * corpo só serve para sabermos que correu bem. Depois de entrar, chamamos
 * `bootstrap()` — que lê `/users/@me` com o cookie — em vez de confiar no que
 * veio no corpo. Uma fonte de verdade só.
 *
 * Em produção este servidor está fechado ao painel admin do Green Village: só
 * entra quem tem cadastro activo lá, com a password de lá. A política vem do
 * servidor (`/auth/policy`) e NÃO de uma variável de build — o mesmo bundle
 * serve o servidor aberto de desenvolvimento e o fechado de produção, e um
 * botão "criar conta" num servidor fechado é uma porta que não existe.
 */

import { Limits } from "@greenvoice/shared";
import { type FormEvent, useEffect, useState } from "react";
import { type AuthPolicy, ApiError, api } from "../lib/api";
import { useStore } from "../store/store";

type Mode = "login" | "register";

/** A recusa que não é "tenta outra vez" — não há password que a resolva. */
interface Blocked {
  message: string;
  hint: string | null;
}

export function AuthScreen() {
  const [mode, setMode] = useState<Mode>("login");
  const [login, setLogin] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [gender, setGender] = useState<"MALE" | "FEMALE">("MALE");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [policy, setPolicy] = useState<AuthPolicy | null>(null);
  const [blocked, setBlocked] = useState<Blocked | null>(null);

  useEffect(() => {
    let alive = true;
    api.auth
      .policy()
      .then((next) => {
        if (alive) setPolicy(next);
      })
      // Se nem a política se consegue ler, a API está em baixo — o próprio
      // login vai falhar a seguir e dizê-lo melhor do que um aviso aqui.
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  const gated = policy?.adminGate === true;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);

    try {
      const result =
        mode === "login"
          ? await api.auth.login({
              login: login.trim(),
              password,
              ...(mfaCode ? { mfaCode: mfaCode.trim() } : {}),
            })
          : await api.auth.register({
              username: username.trim(),
              email: email.trim(),
              password,
              gender,
              ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
            });

      if ("mfaRequired" in result) {
        setMfaRequired(true);
        setError("Digite o código de verificação.");
        return;
      }

      await useStore.getState().bootstrap();
    } catch (caught) {
      if (caught instanceof ApiError && caught.isAdminOnly) {
        // Sai do formulário por completo. Deixar os campos à vista convidava a
        // tentar de novo, e tentar de novo nunca vai dar — não é a password
        // que está errada, é a pessoa que não consta no painel.
        setPassword("");
        setBlocked({ message: caught.message, hint: caught.hint });
        return;
      }
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Não foi possível contatar o servidor. Verifique se a API está rodando.",
      );
    } finally {
      setBusy(false);
    }
  }

  const isLogin = mode === "login";

  if (blocked) {
    return (
      <div className="auth">
        <div className="auth__panel">
          <div className="auth__gate" role="alert">
            <span className="auth__gate-icon" aria-hidden="true">
              🔒
            </span>
            <h1>Acesso restrito</h1>
            <p className="auth__gate-message">{blocked.message}</p>
            {blocked.hint ? <p className="auth__gate-hint">{blocked.hint}</p> : null}
            <button
              className="button button--primary"
              type="button"
              onClick={() => {
                setBlocked(null);
                setError(null);
                setMfaRequired(false);
                setMfaCode("");
              }}
            >
              Tentar com outra conta
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth">
      <div className="auth__panel">
        <header className="auth__head">
          <span className="auth__logo" aria-hidden="true" />
          <h1>GreenVoice</h1>
          <p>{isLogin ? "Bem-vindo de volta." : "Crie a sua conta."}</p>
          {gated ? (
            <p className="auth__badge">
              <span aria-hidden="true">🔒</span> Área restrita à equipe Green Village — entre com o e-mail e a senha do
              painel administrativo.
            </p>
          ) : null}
        </header>

        <form className="auth__form" onSubmit={submit}>
          {isLogin ? (
            <label className="field">
              {/* Sob o portão o e-mail é o caminho certo (é o que o painel
                  conhece), mas o usuário local continua a funcionar para quem
                  já entrou uma vez — daí não forçar `type="email"`. */}
              <span>{gated ? "E-mail do painel Green Village" : "Usuário ou e-mail"}</span>
              <input
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                autoComplete="username"
                autoFocus
                required
              />
            </label>
          ) : (
            <>
              <label className="field">
                <span>Nome de usuário</span>
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  minLength={Limits.username.min}
                  maxLength={Limits.username.max}
                  pattern="[^@#]+"
                  title="Podes usar maiúsculas, acentos e espaços. Só não pode @ nem #."
                  autoComplete="username"
                  autoFocus
                  required
                />
              </label>
              <label className="field">
                <span>Nome de exibição (opcional)</span>
                <input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  maxLength={Limits.displayName.max}
                  autoComplete="nickname"
                />
              </label>
              <label className="field">
                <span>E-mail</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </label>
              <fieldset className="field field--gender">
                <span>Personagem</span>
                <div className="gender-choice" role="radiogroup" aria-label="Género do personagem">
                  <button
                    type="button"
                    className={`gender-choice__option${gender === "MALE" ? " is-active" : ""}`}
                    aria-pressed={gender === "MALE"}
                    onClick={() => setGender("MALE")}
                  >
                    Masculino
                  </button>
                  <button
                    type="button"
                    className={`gender-choice__option${gender === "FEMALE" ? " is-active" : ""}`}
                    aria-pressed={gender === "FEMALE"}
                    onClick={() => setGender("FEMALE")}
                  >
                    Feminino
                  </button>
                </div>
              </fieldset>
            </>
          )}

          <label className="field">
            <span>Senha</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              // Só a REGISTAR. A entrar valida-se uma password que JÁ existe, e uma
              // regra de comprimento aqui não pode melhorar nada — só trancar
              // alguém à porta antes sequer de o pedido sair do browser. Com o
              // portão ligado é pior: a senha escrita aqui é a do painel admin,
              // cuja política não é nossa. O servidor nunca impôs comprimento no
              // login (só em `register` e ao trocar de password), portanto isto
              // era uma regra que existia apenas no browser.
              minLength={isLogin ? undefined : Limits.password.min}
              autoComplete={isLogin ? "current-password" : "new-password"}
              required
            />
          </label>

          {mfaRequired ? (
            <label className="field">
              <span>Código de verificação</span>
              <input
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={8}
                autoFocus
              />
            </label>
          ) : null}

          {error ? (
            <p className="auth__error" role="alert">
              {error}
            </p>
          ) : null}

          <button className="button button--primary" type="submit" disabled={busy}>
            {busy ? "Processando…" : isLogin ? "Entrar" : "Criar conta"}
          </button>
        </form>

        {/* O botão só aparece quando o servidor confirma que há mesmo onde se
            registar. Enquanto a política não chega (`policy === null`) fica
            escondido: piscar um convite e retirá-lo é pior do que nunca o
            mostrar. Num servidor fechado, a conta nasce sozinha ao primeiro
            login de quem consta no painel — não há nada para cadastrar. */}
        {policy?.openRegistration ? (
          <footer className="auth__foot">
            <button
              type="button"
              className="link"
              onClick={() => {
                setMode(isLogin ? "register" : "login");
                setError(null);
                setMfaRequired(false);
              }}
            >
              {isLogin ? "Ainda não tem conta? Cadastre-se" : "Já tem conta? Entre"}
            </button>
          </footer>
        ) : null}
      </div>
    </div>
  );
}
